import { useState } from 'react';

import { type Ns } from '@admin/i18n/types';
import {
    BaseModal,
    BaseModalFooter,
    BaseModalHeader,
    BaseModalType,
    useModalContext,
} from '@admin/organisms/BaseModal';
import { Form } from '@admin/organisms/Form';
import { type FormFields } from '@admin/types';
import FlashMessageUtil from '@admin/utils/FlashMessageUtil/FlashMessageUtil';
import { useTranslation } from '@cms/i18n/client';
import { Button } from '@common/atoms/Button';

export interface Props<Entity, CreateEntity> {
    formFields: FormFields;
    onSuccess: () => void;
    submit: (data: CreateEntity) => Promise<Entity> | Promise<void>;
    __translationKey: Ns | string;
    contextKey?: BaseModalType | string;
    /**
     * render is useful if you want new keys to be pushed to lokalise
     * just add render={true} and refresh your page to push all keys to lokalise
     */
    render?: true;
}

export function CreateModal<Entity, CreateEntity>({
    formFields,
    onSuccess,
    submit,
    __translationKey,
    contextKey = BaseModalType.Create,
    render,
}: Props<Entity, CreateEntity>) {
    const __translate = useTranslation(__translationKey).t;
    const flashes = FlashMessageUtil(__translate);

    const { closeModal } = useModalContext(contextKey);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

    const handleSubmit = (data: CreateEntity) =>
        submit(data)
            .then(closeModal)
            .then(onSuccess)
            .then(flashes.createSuccess)
            .catch(flashes.createError)
            .finally(() => setIsSubmitting(false));

    if (render) {
        flashes.createSuccess();
        flashes.createError();
    }

    const clickButton = () => {
        setIsSubmitting(true);
        doSubmit();
    };

    let doSubmit: () => void;
    const bindDoSubmit = (callback: () => void) => {
        doSubmit = callback;
    };

    return (
        <BaseModal contextKey={contextKey} render={render}>
            <BaseModalHeader contextKey={contextKey} title={__translate('createModal.title')} />
            <Form<Entity, CreateEntity>
                id={'create'}
                action={'create'}
                handleSubmit={handleSubmit}
                bindDoSubmit={bindDoSubmit}
                formFields={formFields}
                onDataInvalid={() => setIsSubmitting(false)}
            />
            <BaseModalFooter className="flex justify-between">
                <Button
                    variant="filled-primary"
                    className="w-full"
                    loading={isSubmitting}
                    type="submit"
                    onClick={clickButton}
                >
                    {__translate('new')}
                </Button>
            </BaseModalFooter>
        </BaseModal>
    );
}
